const defaultOptions = {
  active: true,
  breakpoints: {}
}

function clampNumber(number, min, max) {
  return Math.min(Math.max(number, min), max)
}
function isNumber(value) {
  return typeof value === 'number' && !isNaN(value)
}

function Fade(userOptions = {}) {
  const fullOpacity = 1
  const noOpacity = 0
  const fadeFriction = 0.68
  let emblaApi
  let opacities = []
  let fadeToNextDistance
  let distanceFromPointerDown = 0
  let fadeVelocity = 0
  let progress = 0
  let defaultSettledBehaviour
  let defaultProgressBehaviour
  function init(emblaApiInstance, optionsHandler) {
    emblaApi = emblaApiInstance
    const { mergeOptions, optionsAtMedia } = optionsHandler
    const optionsBase = mergeOptions(defaultOptions, Fade.globalOptions)
    const allOptions = mergeOptions(optionsBase, userOptions)
    optionsAtMedia(allOptions)
    const selectedSnap = emblaApi.selectedScrollSnap()
    const { scrollBody, containerRect, axis } = emblaApi.internalEngine()
    const containerSize = axis.measureSize(containerRect)
    fadeToNextDistance = clampNumber(containerSize * 0.8, 200, 500)
    opacities = emblaApi
      .scrollSnapList()
      .map((_, index) => (index === selectedSnap ? fullOpacity : noOpacity))
    disableScroll()
    fadeToSelectedSnapInstantly()
    defaultSettledBehaviour = scrollBody.settled
    defaultProgressBehaviour = emblaApi.scrollProgress
    scrollBody.settled = settled
    emblaApi.scrollProgress = scrollProgress
    emblaApi
      .on('select', select)
      .on('slideFocus', fadeToSelectedSnapInstantly)
      .on('pointerDown', pointerDown)
  }
  function destroy() {
    const { scrollBody } = emblaApi.internalEngine()
    scrollBody.settled = defaultSettledBehaviour
    emblaApi.scrollProgress = defaultProgressBehaviour
    emblaApi
      .off('select', select)
      .off('slideFocus', fadeToSelectedSnapInstantly)
      .off('pointerDown', pointerDown)
    emblaApi.slideNodes().forEach((slideNode) => {
      const slideStyle = slideNode.style
      slideStyle.opacity = ''
      slideStyle.transform = ''
      slideStyle.pointerEvents = ''
      if (!slideNode.getAttribute('style')) slideNode.removeAttribute('style')
    })
  }
  function fadeToSelectedSnapInstantly() {
    const selectedSnap = emblaApi.selectedScrollSnap()
    setOpacities(selectedSnap, fullOpacity)
  }
  function pointerDown() {
    distanceFromPointerDown = 0
    fadeVelocity = 0
  }
  function select() {
    const duration = emblaApi.internalEngine().scrollBody.duration()
    fadeVelocity = duration ? 0 : fullOpacity
    if (!duration) fadeToSelectedSnapInstantly()
  }
  function getSlideTransform(position) {
    const { axis } = emblaApi.internalEngine()
    const translateAxis = axis.scroll.toUpperCase()
    return `translate${translateAxis}(${direction(position)}px)`
  }
  function disableScroll() {
    const { translate, slideLooper } = emblaApi.internalEngine()
    translate.clear()
    translate.toggleActive(false)
    slideLooper.loopPoints.forEach(({ translate }) => {
      translate.clear()
      translate.toggleActive(false)
    })
  }
  function lockExcessiveScroll(fadeIndex) {
    const { scrollSnaps, location, target } = emblaApi.internalEngine()
    if (!isNumber(fadeIndex) || opacities[fadeIndex] < 0.5) return
    location.set(scrollSnaps[fadeIndex])
    target.set(location)
  }
  function setOpacities(fadeIndex, velocity) {
    const scrollSnaps = emblaApi.scrollSnapList()
    scrollSnaps.forEach((_, index) => {
      const absVelocity = Math.abs(velocity)
      const currentOpacity = opacities[index]
      const isFadeIndex = index === fadeIndex
      const nextOpacity = isFadeIndex
        ? currentOpacity + absVelocity
        : currentOpacity - absVelocity
      const clampedOpacity = clampNumber(nextOpacity, noOpacity, fullOpacity)
      opacities[index] = clampedOpacity
      if (isFadeIndex) setProgress(fadeIndex, clampedOpacity)
      setOpacity(index)
    })
  }
  function direction(n) {
    return n * 1
  }
  function setOpacity(index) {
    const slidesInSnap = emblaApi.internalEngine().slideRegistry[index]
    const { scrollSnaps, containerRect } = emblaApi.internalEngine()
    const opacity = opacities[index]
    slidesInSnap.forEach((slideIndex) => {
      const slideStyle = emblaApi.slideNodes()[slideIndex].style
      const roundedOpacity = parseFloat(opacity.toFixed(2))
      const hasOpacity = roundedOpacity > noOpacity
      const position = hasOpacity ? scrollSnaps[index] : containerRect.width + 2
      const transform = getSlideTransform(position)
      if (hasOpacity) slideStyle.transform = transform
      slideStyle.opacity = roundedOpacity.toString()
      slideStyle.pointerEvents = opacity > 0.5 ? 'auto' : 'none'
      if (!hasOpacity) slideStyle.transform = transform
    })
  }
  function setProgress(fadeIndex, opacity) {
    const { index, dragHandler, scrollSnaps } = emblaApi.internalEngine()
    const pointerDown = dragHandler.pointerDown()
    const snapFraction = 1 / (scrollSnaps.length - 1)
    let indexA = fadeIndex
    let indexB = pointerDown
      ? emblaApi.selectedScrollSnap()
      : emblaApi.previousScrollSnap()
    if (pointerDown && indexA === indexB) {
      const reverseSign = Math.sign(distanceFromPointerDown) * -1
      indexA = indexB
      indexB = index.clone().set(indexB).add(reverseSign).get()
    }
    const currentPosition = indexB * snapFraction
    const diffPosition = (indexA - indexB) * snapFraction
    progress = currentPosition + diffPosition * opacity
  }
  function getFadeIndex() {
    const { dragHandler, scrollBody, index } = emblaApi.internalEngine()
    const selectedSnap = emblaApi.selectedScrollSnap()
    if (!dragHandler.pointerDown()) return selectedSnap
    const directionSign = Math.sign(scrollBody.velocity())
    const distanceSign = Math.sign(distanceFromPointerDown)
    const nextSnap = index
      .clone()
      .set(selectedSnap)
      .add(directionSign * -1)
      .get()
    if (!directionSign || !distanceSign) return null
    return distanceSign === directionSign ? nextSnap : selectedSnap
  }
  const fade = (emblaApi) => {
    const { dragHandler, scrollBody } = emblaApi.internalEngine()
    const pointerDown = dragHandler.pointerDown()
    const velocity = scrollBody.velocity()
    const duration = scrollBody.duration()
    const fadeIndex = getFadeIndex()
    const noFadeIndex = !isNumber(fadeIndex)
    if (pointerDown) {
      if (!velocity) return
      distanceFromPointerDown += velocity
      fadeVelocity = Math.abs(velocity / fadeToNextDistance)
      lockExcessiveScroll(fadeIndex)
    }
    if (!pointerDown) {
      if (!duration || noFadeIndex) return
      fadeVelocity += (fullOpacity - opacities[fadeIndex]) / duration
      fadeVelocity *= fadeFriction
    }
    if (noFadeIndex) return
    setOpacities(fadeIndex, fadeVelocity)
  }
  function settled() {
    const { target, location } = emblaApi.internalEngine()
    const diffToTarget = target.get() - location.get()
    const notReachedTarget = Math.abs(diffToTarget) >= 1
    const fadeIndex = getFadeIndex()
    const noFadeIndex = !isNumber(fadeIndex)
    fade(emblaApi)
    if (noFadeIndex || notReachedTarget) return false
    return opacities[fadeIndex] > 0.999
  }
  function scrollProgress() {
    return progress
  }
  const self = {
    name: 'fade',
    options: userOptions,
    init,
    destroy
  }
  return self
}
Fade.globalOptions = undefined

class ProductGallery487 extends HTMLElement {
  static observedAttributes = ['color', 'clientWidth'];

  constructor() {
    super();
    this.observeSelectedVariantAttribute();
  }

  connectedCallback() {
    var comp = this;
    this.elements = {
      prevButton: comp.querySelector('.prev-button') ?? null,
      nextButton: comp.querySelector('.next-button') ?? null,
      dotsContainer: comp.querySelector('.dots-container') ?? null,
    };

    this.options = {
      axis: 'x',
      align: 'start',
      container: `[data-track]`,
      slides: `[data-slide]`,
      loop: false,
      duration: 10
    };

    this.option_thumbs = {
      axis: "y",
      align: 'center',
      containScroll: 'keepSnaps',
      dragFree: true,
    };

    this.viewportNodeMainCarousel = this.querySelector('.main-viewport');
    this.viewportNodeThumbCarousel = this.querySelector('.thumbnail-viewport');

    this.init();
    this.setupDots();
    this.handleInitialVariantSelection();
    this.handleResize();
    window.addEventListener("resize", this.handleResize);
  }

  observeSelectedVariantAttribute() {
    const closestElement = this.closest('[data-product-card-selected_variant_id]');

    if (!closestElement) {
      console.error("No ancestor element found with 'data-product-card-selected_variant_id' attribute.");
      return;
    }

    const observer = new MutationObserver(mutationsList => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-product-card-selected_variant_id') {
          const newValue = closestElement.getAttribute('data-product-card-selected_variant_id');
          if (newValue) {
            this.scrollToSlide(newValue);
          }
        }
      }
    });

    observer.observe(closestElement, { attributes: true });
  }

  handleInitialVariantSelection() {
    const closestElement = this.closest('[data-product-card-selected_variant_id]');
    if (!closestElement) {
      console.warn("No ancestor element found with 'data-product-card-selected_variant_id' attribute for initial variant selection.");
      return;
    }

    const initialVariantId = closestElement.getAttribute('data-product-card-selected_variant_id');
    if (initialVariantId) {
      this.scrollToSlide(initialVariantId);
    }
  }

  scrollToSlide(selectedVariantId) {
    const targetSlideIndex = this.findSlideIndexByVariantId(selectedVariantId);
    if (targetSlideIndex !== -1) {
      this.emblaApiMain.scrollTo(targetSlideIndex, false);
      this.emblaApiThumb.scrollTo(targetSlideIndex, false);
    }
  }

  findSlideIndexByVariantId(variantId) {
    const slides = this.emblaApiMain.slideNodes();
    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i];
      const associatedVariantId = slide.dataset.targetVariant;
      if (associatedVariantId && associatedVariantId === variantId) {
        return i;
      }
    }
    return -1;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    console.log(`Attribute ${name} has changed.`);
  }

  destroy() {
    window.removeEventListener("resize", this.handleResize);
    this.emblaApiMain.destroy();
    this.emblaApiThumb.destroy();
  }

  init() {
    this.emblaApiMain = EmblaCarousel(
      this.viewportNodeMainCarousel,
      this.options,
      [
        Fade()
      ]
    );
    this.emblaApiThumb = EmblaCarousel(this.viewportNodeThumbCarousel, {
      ...this.options,
      ...this.option_thumbs,
    });

    const removeThumbBtnsClickHandlers = this.addThumbBtnsClickHandlers();
    const removeToggleThumbBtnsActive = this.addToggleThumbBtnsActive();
    const removePrevNextBtnsClickHandlers = this.addPrevNextBtnsClickHandlers(
      this.emblaApiMain,
      this.elements.prevButton,
      this.elements.nextButton
    );

    this.emblaApiMain.on('select', () => this.updateDots());

    this.emblaApiMain
      .on('destroy', removeThumbBtnsClickHandlers)
      .on('destroy', removeToggleThumbBtnsActive);

    this.emblaApiThumb
      .on('destroy', removeThumbBtnsClickHandlers)
      .on('destroy', removeToggleThumbBtnsActive);
  }

  addThumbBtnsClickHandlers() {
    const slidesThumbs = this.emblaApiThumb.slideNodes();

    const scrollToIndex = slidesThumbs.map((_, index) => () => {
      this.emblaApiMain.scrollTo(index, false);
    });

    slidesThumbs.forEach((slideNode, index) => {
      slideNode.addEventListener('click', scrollToIndex[index], false);
    });

    return () => {
      slidesThumbs.forEach((slideNode, index) => {
        slideNode.removeEventListener('click', scrollToIndex[index], false);
      });
    };
  }

  setupDots() {
    const dotsContainer = this.elements.dotsContainer;
    if (!dotsContainer) return;

    const slides = this.emblaApiMain.slideNodes();

    if (slides.length <= 1) {
      dotsContainer.classList.add("!hidden");
      return;
    }

    dotsContainer.innerHTML = ""; // Clear any existing dots

    slides.forEach((_, index) => {
      const dot = document.createElement("button");
      dot.className = "dot";
      dot.addEventListener("click", () => this.emblaApiMain.scrollTo(index));
      dotsContainer.appendChild(dot);
    });

    this.updateDots();
  }

  updateDots() {
    const dotsContainer = this.elements.dotsContainer;
    if (!dotsContainer) return;

    const selectedIndex = this.emblaApiMain.selectedScrollSnap();
    dotsContainer.querySelectorAll(".dot").forEach((dot, index) => {
      dot.classList.toggle("active", index === selectedIndex);
    });
  }

  addToggleThumbBtnsActive() {
    const slidesThumbs = this.emblaApiThumb.slideNodes();

    const toggleThumbBtnsState = () => {
      const selected = this.emblaApiMain.selectedScrollSnap();
      const previous = this.emblaApiMain.previousScrollSnap();
      this.emblaApiThumb.scrollTo(selected);
      slidesThumbs[previous].classList.remove('selected');
      slidesThumbs[selected].classList.add('selected');
    };

    this.emblaApiMain.on('select', toggleThumbBtnsState);
    this.emblaApiThumb.on('init', toggleThumbBtnsState);

    return () => {
      const selected = this.emblaApiMain.selectedScrollSnap();
      slidesThumbs[selected].classList.remove('selected');
    };
  }

  addTogglePrevNextBtnsActive = (emblaApi, prevBtn, nextBtn) => {
    const togglePrevNextBtnsState = () => {
      if (emblaApi.canScrollPrev()) prevBtn.removeAttribute('disabled');
      else prevBtn.setAttribute('disabled', 'disabled');

      if (emblaApi.canScrollNext()) nextBtn.removeAttribute('disabled');
      else nextBtn.setAttribute('disabled', 'disabled');
    };

    emblaApi
      .on('select', togglePrevNextBtnsState)
      .on('init', togglePrevNextBtnsState)
      .on('reInit', togglePrevNextBtnsState);

    return () => {
      prevBtn.removeAttribute('disabled');
      nextBtn.removeAttribute('disabled');
    };
  };

  addPrevNextBtnsClickHandlers = (emblaApi, prevBtn, nextBtn) => {
    const scrollPrev = () => emblaApi.scrollPrev();
    const scrollNext = () => emblaApi.scrollNext();

    if (!prevBtn) {
      return
    }
    if (!nextBtn) {
      return
    }

    prevBtn?.addEventListener('click', scrollPrev, false);
    nextBtn?.addEventListener('click', scrollNext, false);

    const removeTogglePrevNextBtnsActive = this.addTogglePrevNextBtnsActive(
      emblaApi,
      prevBtn,
      nextBtn
    );

    return () => {
      removeTogglePrevNextBtnsActive();
      prevBtn.removeEventListener('click', scrollPrev, false);
      nextBtn.removeEventListener('click', scrollNext, false);
    };
  };

  handleResize = () => {
    const mainViewport = this.viewportNodeMainCarousel;
    if (!mainViewport) return;

    const width = window.innerWidth;

    if (this._lastWidth === width) return;
    this._lastWidth = width;

    const newAxis = width >= 768 ? "y" : "x";

    if (this._currentThumbAxis !== newAxis) {
      this._currentThumbAxis = newAxis;

      this.emblaApiThumb.reInit({
        ...this.options,
        ...this.option_thumbs,
        axis: newAxis,
      });
    }

    const height = mainViewport.offsetHeight;

    if (!height || height <= 0) return;

    document
      .querySelectorAll(".thumbnail-viewport")
      .forEach((thumbViewport) => {
        thumbViewport.style.setProperty(
          "--thumbnails-max-height-lg",
          `${height}px`
        );
      });
  };
}

customElements.define('product-gallery-487', ProductGallery487);